import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface RollingNumberProps {
  /** Target numeric value. The component tweens to it. */
  value: number;
  /** Formats the (possibly fractional) animated value for display. */
  format?: (n: number) => string;
  /** Tween duration in ms. */
  durationMs?: number;
  className?: string;
  /** Accessible label for the final value (animation frames are aria-hidden). */
  'aria-label'?: string;
}

/**
 * Count-up / odometer number. On first appearance it rolls from 0 to `value`;
 * on later updates it tweens from the currently-shown value to the new one.
 * Honors `prefers-reduced-motion` by snapping. Pair with a `Skeleton` while the
 * value is still loading — this component is for the "value just arrived" moment.
 */
export function RollingNumber({
  value,
  format = (n) => String(Math.round(n)),
  durationMs = 600,
  className,
  'aria-label': ariaLabel,
}: RollingNumberProps) {
  const [display, setDisplay] = useState(value);
  const displayRef = useRef(value);
  const hasMounted = useRef(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = hasMounted.current ? displayRef.current : 0;
    hasMounted.current = true;

    if (prefersReducedMotion() || durationMs <= 0 || from === value) {
      displayRef.current = value;
      setDisplay(value);
      return;
    }

    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const next = from + (value - from) * easeOutCubic(t);
      displayRef.current = next;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        displayRef.current = value;
        setDisplay(value);
      }
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [value, durationMs]);

  return (
    <span className={cn('tabular-nums', className)}>
      <span aria-hidden>{format(display)}</span>
      <span className='sr-only'>{ariaLabel ?? format(value)}</span>
    </span>
  );
}
